import { toast } from 'react-toastify';

/**
 * KYC Utility Functions
 * Enhanced file handling and validation utilities for KYC verification
 */

/**
 * Convert file to Base64 (enhanced version of existing Base64File)
 * @param {File} file - File to convert
 * @param {Object} options - Conversion options
 * @returns {Promise<string>} Base64 string
 */
export function fileToBase64(file, options = {}) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = () => {
        let result = reader.result;
        
        // Apply compression if requested
        if (options.compress && file.type.startsWith('image/')) {
          compressImage(result, options.quality || 0.8, options.maxWidth || 1920)
            .then(compressedResult => resolve(compressedResult))
            .catch(error => reject(error));
        } else {
          resolve(result);
        }
      };
      
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Compress image before upload
 * @param {string} dataURL - Base64 image data
 * @param {number} quality - Compression quality (0-1)
 * @param {number} maxWidth - Maximum width
 * @returns {Promise<string>} Compressed base64 string
 */
export function compressImage(dataURL, quality = 0.8, maxWidth = 1920) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataURL);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataURL;
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Validate file type for KYC documents
 * @param {File} file - File to validate
 * @param {Array} allowedTypes - Allowed MIME types
 * @returns {Object} Validation result
 */
export function validateFileType(file, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']) {
  const isValid = allowedTypes.includes(file.type);
  
  return {
    isValid,
    message: isValid ? '' : `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`
  };
}

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {Object} Validation result
 */
export function validateFileSize(file, maxSizeMB = 5) {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const isValid = file.size <= maxSizeBytes;
  
  return {
    isValid,
    message: isValid ? '' : `File size exceeds ${maxSizeMB}MB limit`
  };
}

/**
 * Comprehensive file validation for KYC documents
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export function validateKYCDocument(file, options = {}) {
  const {
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
    maxSizeMB = 5,
    required = true
  } = options;
  
  if (!file && required) {
    return {
      isValid: false,
      message: 'File is required'
    };
  }
  
  if (!file) {
    return { isValid: true, message: '' };
  }
  
  // Check file type
  const typeValidation = validateFileType(file, allowedTypes);
  if (!typeValidation.isValid) {
    return typeValidation;
  }
  
  // Check file size
  const sizeValidation = validateFileSize(file, maxSizeMB);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }
  
  return { isValid: true, message: '' };
}

/**
 * Generate file preview URL
 * @param {File} file - File to preview
 * @returns {Promise<string>} Preview URL
 */
export function generateFilePreview(file) {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } else {
      // For non-image files, return a placeholder
      resolve(null);
    }
  });
}

/**
 * Create file object for API upload
 * @param {File} file - Original file
 * @param {string} base64 - Base64 string
 * @param {string} documentType - Type of document
 * @returns {Object} File object for API
 */
export function createFileObject(file, base64, documentType) {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    base64: base64,
    documentType: documentType,
    uploadDate: new Date().toISOString()
  };
}

/**
 * PAN Number validation
 * @param {string} panNumber - PAN number to validate
 * @returns {Object} Validation result
 */
export function validatePANNumber(panNumber) {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const isValid = panRegex.test(panNumber);
  
  return {
    isValid,
    message: isValid ? '' : 'Invalid PAN format. Format: ABCDE1234F'
  };
}

/**
 * Aadhaar Number validation
 * @param {string} aadhaarNumber - Aadhaar number to validate
 * @returns {Object} Validation result
 */
export function validateAadhaarNumber(aadhaarNumber) {
  // Remove spaces and hyphens
  const cleanNumber = aadhaarNumber.replace(/[\s-]/g, '');
  
  // Check if it's 12 digits
  const digitRegex = /^\d{12}$/;
  if (!digitRegex.test(cleanNumber)) {
    return {
      isValid: false,
      message: 'Aadhaar number must be 12 digits'
    };
  }
  
  // Check if it doesn't start with 0 or 1
  if (cleanNumber.startsWith('0') || cleanNumber.startsWith('1')) {
    return {
      isValid: false,
      message: 'Aadhaar number cannot start with 0 or 1'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
}

/**
 * IFSC Code validation
 * @param {string} ifscCode - IFSC code to validate
 * @returns {Object} Validation result
 */
export function validateIFSCCode(ifscCode) {
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const isValid = ifscRegex.test(ifscCode);
  
  return {
    isValid,
    message: isValid ? '' : 'Invalid IFSC format. Format: SBIN0001234'
  };
}

/**
 * Bank Account Number validation
 * @param {string} accountNumber - Account number to validate
 * @returns {Object} Validation result
 */
export function validateBankAccountNumber(accountNumber) {
  // Remove spaces and special characters
  const cleanNumber = accountNumber.replace(/[\s-]/g, '');
  
  // Check if it's 9-18 digits
  const accountRegex = /^[0-9]{9,18}$/;
  const isValid = accountRegex.test(cleanNumber);
  
  return {
    isValid,
    message: isValid ? '' : 'Account number must be 9-18 digits'
  };
}

/**
 * Name validation (for PAN, Aadhaar, Bank)
 * @param {string} name - Name to validate
 * @returns {Object} Validation result
 */
export function validateName(name) {
  const nameRegex = /^[A-Za-z\s]+$/;
  const isValid = nameRegex.test(name) && name.trim().length >= 2;
  
  return {
    isValid,
    message: isValid ? '' : 'Name must contain only alphabets and spaces (minimum 2 characters)'
  };
}

/**
 * Date of Birth validation
 * @param {string} dateOfBirth - Date of birth to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export function validateDateOfBirth(dateOfBirth, options = {}) {
  const { minAge = 18, maxAge = 100 } = options;
  
  if (!dateOfBirth) {
    return {
      isValid: false,
      message: 'Date of birth is required'
    };
  }
  
  const dob = new Date(dateOfBirth);
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  
  if (isNaN(dob.getTime())) {
    return {
      isValid: false,
      message: 'Invalid date format'
    };
  }
  
  if (age < minAge) {
    return {
      isValid: false,
      message: `Age must be at least ${minAge} years`
    };
  }
  
  if (age > maxAge) {
    return {
      isValid: false,
      message: `Age cannot exceed ${maxAge} years`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Generate unique file name
 * @param {string} originalName - Original file name
 * @param {string} prefix - Prefix for the file name
 * @returns {string} Unique file name
 */
export function generateUniqueFileName(originalName, prefix = 'kyc') {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  const nameWithoutExt = originalName.replace(/\.[^/.]+$/, "");
  const cleanName = nameWithoutExt.replace(/[^a-zA-Z0-9]/g, '_');
  
  return `${prefix}_${cleanName}_${timestamp}.${extension}`;
}

/**
 * Debounce function for search and validation
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Progress tracker for file uploads
 */
export class UploadProgressTracker {
  constructor() {
    this.progress = 0;
    this.isUploading = false;
    this.callbacks = [];
  }
  
  addCallback(callback) {
    this.callbacks.push(callback);
  }
  
  updateProgress(progress) {
    this.progress = progress;
    this.callbacks.forEach(callback => callback(progress));
  }
  
  startUpload() {
    this.isUploading = true;
    this.progress = 0;
  }
  
  finishUpload() {
    this.isUploading = false;
    this.progress = 100;
  }
  
  reset() {
    this.progress = 0;
    this.isUploading = false;
  }
}

/**
 * Error handler for KYC operations
 * @param {Error} error - Error object
 * @param {string} operation - Operation being performed
 */
export function handleKYCError(error, operation = 'KYC operation') {
  console.error(`${operation} error:`, error);
  
  let message = 'An unexpected error occurred';
  
  if (error.message) {
    message = error.message;
  } else if (error.response?.data?.message) {
    message = error.response.data.message;
  }
  
  toast.error(`${operation}: ${message}`);
}

/**
 * Success handler for KYC operations
 * @param {string} message - Success message
 * @param {string} operation - Operation performed
 */
export function handleKYCSuccess(message, operation = 'KYC operation') {
  const successMessage = message || `${operation} completed successfully`;
  toast.success(successMessage);
}

const kycUtils = {
  fileToBase64,
  compressImage,
  validateFileType,
  validateFileSize,
  validateKYCDocument,
  generateFilePreview,
  createFileObject,
  validatePANNumber,
  validateAadhaarNumber,
  validateIFSCCode,
  validateBankAccountNumber,
  validateName,
  validateDateOfBirth,
  formatFileSize,
  generateUniqueFileName,
  debounce,
  UploadProgressTracker,
  handleKYCError,
  handleKYCSuccess
};

export default kycUtils;
